// Composable for managing per-page spotlight-style hint tooltips.
// Tracks seen state via the user store (server-persisted preferences).
import { computed, onUnmounted, ref, watch } from "vue";
import type { Ref } from "vue";

import { useUserStore } from "@/stores/user-store";

export interface HintStep {
    /** Unique ID used to track whether this hint has been seen. */
    id: string;
    /** Ref to the DOM element this hint should be anchored to. */
    targetRef: Ref<HTMLElement | null | undefined>;
    title: string;
    content: string;
}

// True while the active page has at least one unseen hint step.
// The page transition uses mode="out-in" so at most one page is mounted at a time,
// making a simple boolean (not a counter) sufficient.
const _pageHintActive = ref(false);

export const usePageHints = (steps: HintStep[], priority = 0) => {
    const userStore = useUserStore();

    // Filters out steps the user has already seen (or when hints are disabled).
    // Always shows the first pending step, so marking a step seen automatically
    // advances to the next one without manual index tracking.
    const pendingSteps = computed(() =>
        steps.filter(
            (s) => userStore.hintsEnabled && !userStore.seenHints.includes(s.id)
        )
    );

    if (priority === 0) {
        // Page-level instances own the _pageHintActive flag.
        watch(
            () => pendingSteps.value.length > 0,
            (hasPending) => {
                _pageHintActive.value = hasPending;
            },
            { immediate: true }
        );
        onUnmounted(() => {
            _pageHintActive.value = false;
        });
    }

    const currentStep = computed(() => {
        // Lower-priority instances (layout) defer while any page hint is active.
        if (priority > 0 && _pageHintActive.value) return null;
        return pendingSteps.value[0] ?? null;
    });

    const isVisible = computed(() => currentStep.value !== null);

    const isLastStep = computed(() => pendingSteps.value.length === 1);

    const nextStep = async () => {
        const step = currentStep.value;
        if (!step) return;
        await userStore.markHintSeen(step.id);
        // pendingSteps re-evaluates automatically; currentStep becomes the new first
    };

    const dismissAll = async () => {
        await userStore.setHintsEnabled(false);
    };

    return { currentStep, isVisible, isLastStep, nextStep, dismissAll };
};
